import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  sort_order: number;
  is_active: boolean;
}

const emptyFAQ = { question: "", answer: "", sort_order: 0, is_active: true };

export default function LandingFAQ() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Partial<FAQ> | null>(null);
  const [open, setOpen] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["landing_faq"],
    queryFn: async () => {
      const { data, error } = await supabase.from("landing_faq").select("*").order("sort_order");
      if (error) throw error;
      return data as FAQ[];
    },
  });

  const save = useMutation({
    mutationFn: async (r: Partial<FAQ>) => {
      if (r.id) {
        const { error } = await supabase.from("landing_faq").update(r).eq("id", r.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("landing_faq").insert([r as any]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["landing_faq"] });
      setOpen(false);
      toast.success("Сохранено");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("landing_faq").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["landing_faq"] });
      toast.success("Удалено");
    },
  });

  return (
    <AdminLayout title="FAQ на лендинге">
      <div className="flex justify-between items-center mb-6">
        <p className="text-muted-foreground text-sm">Управление часто задаваемыми вопросами</p>
        <Button onClick={() => { setEditing({ ...emptyFAQ }); setOpen(true); }} size="sm"><Plus className="w-4 h-4 mr-1" />Добавить</Button>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">#</TableHead>
              <TableHead>Вопрос</TableHead>
              <TableHead>Активен</TableHead>
              <TableHead className="w-24">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Загрузка...</TableCell></TableRow>
            ) : items.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.sort_order}</TableCell>
                <TableCell className="font-medium max-w-md truncate">{r.question}</TableCell>
                <TableCell>{r.is_active ? "✅" : "❌"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditing({ ...r }); setOpen(true); }}><Pencil className="w-3 h-3" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => { if(confirm("Удалить?")) remove.mutate(r.id); }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Редактировать вопрос" : "Новый вопрос"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Вопрос</Label><Input value={editing?.question || ""} onChange={e => setEditing(p => ({...p!, question: e.target.value}))} /></div>
            <div><Label>Ответ</Label><Textarea rows={5} value={editing?.answer || ""} onChange={e => setEditing(p => ({...p!, answer: e.target.value}))} /></div>
            <div className="flex gap-4">
              <div className="flex-1"><Label>Порядок</Label><Input type="number" value={editing?.sort_order || 0} onChange={e => setEditing(p => ({...p!, sort_order: +e.target.value}))} /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={editing?.is_active ?? true} onCheckedChange={v => setEditing(p => ({...p!, is_active: v}))} />
                <Label>Активен</Label>
              </div>
            </div>
            <Button className="w-full" onClick={() => editing && save.mutate(editing)} disabled={save.isPending}>
              {save.isPending ? "Сохранение..." : "Сохранить"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
